/**
 * storage-adapter.ts — Cross-platform storage abstraction for Office Add-ins
 *
 * Provides a unified storage API that works reliably across Windows, Mac, and Web.
 * Uses OfficeRuntime.storage (recommended for Office Add-ins) with fallback to
 * localStorage for backward compatibility.
 *
 * Mac compatibility: Office on Mac uses WKWebView which may have localStorage
 * restrictions. OfficeRuntime.storage is the preferred solution.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Storage interface for getting and setting key-value pairs.
 */
interface StorageAdapter {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
  getAllKeys(): Promise<string[]>;
}

/**
 * OfficeRuntime.storage adapter (preferred for Office Add-ins).
 * Works across all platforms including Mac.
 */
class OfficeRuntimeStorageAdapter implements StorageAdapter {
  async getItem(key: string): Promise<string | null> {
    try {
      const result = await (window as any).OfficeRuntime.storage.getItem(key);
      return result ?? null;
    } catch (err) {
      console.warn(`OfficeRuntime.storage.getItem failed for key "${key}":`, err);
      return null;
    }
  }

  async setItem(key: string, value: string): Promise<void> {
    try {
      await (window as any).OfficeRuntime.storage.setItem(key, value);
    } catch (err) {
      console.error(`OfficeRuntime.storage.setItem failed for key "${key}":`, err);
      throw err;
    }
  }

  async removeItem(key: string): Promise<void> {
    try {
      await (window as any).OfficeRuntime.storage.removeItem(key);
    } catch (err) {
      console.warn(`OfficeRuntime.storage.removeItem failed for key "${key}":`, err);
    }
  }

  async getAllKeys(): Promise<string[]> {
    try {
      const keys = await (window as any).OfficeRuntime.storage.getKeys();
      return keys ?? [];
    } catch (err) {
      console.warn("OfficeRuntime.storage.getKeys failed:", err);
      return [];
    }
  }
}

/**
 * localStorage adapter (fallback for older environments).
 */
class LocalStorageAdapter implements StorageAdapter {
  async getItem(key: string): Promise<string | null> {
    try {
      return localStorage.getItem(key);
    } catch (err) {
      console.warn(`localStorage.getItem failed for key "${key}":`, err);
      return null;
    }
  }

  async setItem(key: string, value: string): Promise<void> {
    try {
      localStorage.setItem(key, value);
    } catch (err) {
      console.error(`localStorage.setItem failed for key "${key}":`, err);
      throw err;
    }
  }

  async removeItem(key: string): Promise<void> {
    try {
      localStorage.removeItem(key);
    } catch (err) {
      console.warn(`localStorage.removeItem failed for key "${key}":`, err);
    }
  }

  async getAllKeys(): Promise<string[]> {
    try {
      return Object.keys(localStorage);
    } catch (err) {
      console.warn("localStorage.keys() failed:", err);
      return [];
    }
  }
}

/**
 * Detect which storage adapter to use.
 * Prefers OfficeRuntime.storage (works on all platforms including Mac).
 * Falls back to localStorage for backward compatibility.
 */
function createStorageAdapter(): StorageAdapter {
  const hasOfficeRuntime =
    typeof window !== "undefined" &&
    (window as any).OfficeRuntime?.storage !== undefined;

  if (hasOfficeRuntime) {
    return new OfficeRuntimeStorageAdapter();
  }

  const hasLocalStorage =
    typeof window !== "undefined" && window.localStorage !== undefined;

  if (hasLocalStorage) {
    return new LocalStorageAdapter();
  }

  // Should never happen in browser environment, but TypeScript needs coverage
  throw new Error("No storage mechanism available");
}

/**
 * Singleton storage adapter instance.
 * Uses OfficeRuntime.storage on Mac and other platforms for reliability.
 */
export const storage: StorageAdapter = createStorageAdapter();
